import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Package, Plus, Search, Edit3, Trash2, Copy, Upload, X, ChevronDown,
  Grid3X3, List, Barcode, QrCode, AlertTriangle
} from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatCurrency, generateId, paginate } from '../utils/helpers';
import { toast } from '../components/Toast';
import { Pagination } from '../components/UIComponents';

const PER_PAGE = 12;

const categories = ['All', 'CCTV', 'NVR', 'Smart Home', 'Alarm', 'Networking', 'Starlink'];

export default function ProductsPage() {
  const { products, addProduct, updateProduct, deleteProduct, duplicateProduct } = useAdminStore();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [showAdd, setShowAdd] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '', category: 'CCTV', brand: '', model: '', price: 0, costPrice: 0, stock: 0, warranty: 12, sku: '', specifications: '',
  });

  const [page, setPage] = useState(1);
  const filtered = products.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase()) || p.brand.toLowerCase().includes(search.toLowerCase());
    const matchCat = selectedCategory === 'All' || p.category === selectedCategory;
    return matchSearch && matchCat;
  });
  useEffect(() => { setPage(1); }, [search, selectedCategory]);
  const { items: pagedProducts, pagination } = useMemo(() => paginate(filtered, page, PER_PAGE), [filtered, page]);

  const lowStockCount = products.filter(p => p.stock - p.reservedStock <= 5).length;

  const handleAdd = () => {
    if (!newProduct.name.trim()) { toast.error('Validation Error', 'Product name is required.'); return; }
    if (!newProduct.sku.trim()) { toast.error('Validation Error', 'SKU is required.'); return; }
    if (newProduct.price <= 0) { toast.error('Validation Error', 'Price must be greater than 0.'); return; }
    const product = {
      id: generateId(),
      ...newProduct,
      reservedStock: 0,
      images: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    addProduct(product as any);
    toast.success('Product Added', `${newProduct.name} has been added to the catalogue.`);
    setNewProduct({ name: '', category: 'CCTV', brand: '', model: '', price: 0, costPrice: 0, stock: 0, warranty: 12, sku: '', specifications: '' });
    setShowAdd(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Products</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{products.length} products · {lowStockCount} low stock alerts</p>
        </div>
        <div className="flex gap-2">
          <button className="h-10 px-4 rounded-xl bg-white border border-gray-200/60 text-[13px] text-gray-600 font-medium hover:bg-gray-50 flex items-center gap-2">
            <Upload className="w-4 h-4" />Import
          </button>
          <button onClick={() => setShowAdd(true)} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2">
            <Plus className="w-4 h-4" />Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products, SKU, brand..."
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
        </div>
        <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
          {categories.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`px-3 h-10 text-[12px] font-medium transition-all ${selectedCategory === cat ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
              {cat}
            </button>
          ))}
        </div>
        <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
          <button onClick={() => setView('grid')} className={`w-10 h-10 flex items-center justify-center ${view === 'grid' ? 'bg-gray-900 text-white' : 'text-gray-400'}`}>
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button onClick={() => setView('list')} className={`w-10 h-10 flex items-center justify-center ${view === 'list' ? 'bg-gray-900 text-white' : 'text-gray-400'}`}>
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Products Grid */}
      {filtered.length === 0 && (
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-12 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-[15px] font-semibold text-gray-500">
            {search || selectedCategory !== 'All' ? 'No products match your filter' : 'No products yet'}
          </p>
          <p className="text-[12px] text-gray-400 mt-1">Add your first product to start building the catalogue.</p>
        </div>
      )}
      {view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {pagedProducts.map((product, i) => (
            <motion.div key={product.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 hover:shadow-lg hover:shadow-gray-200/40 transition-all group">
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="p-1.5 rounded-lg hover:bg-gray-100"><Edit3 className="w-3.5 h-3.5 text-gray-400" /></button>
                  <button onClick={() => duplicateProduct(product.id)} className="p-1.5 rounded-lg hover:bg-gray-100"><Copy className="w-3.5 h-3.5 text-gray-400" /></button>
                  <button onClick={() => deleteProduct(product.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                </div>
              </div>
              <div className="mb-3">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{product.brand}</span>
                <h3 className="text-[14px] font-semibold text-gray-900 mt-0.5 leading-tight">{product.name}</h3>
                <p className="text-[12px] text-gray-400 mt-0.5">{product.model}</p>
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-[10px] font-medium text-blue-600">{product.category}</span>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-600">{product.warranty}mo warranty</span>
              </div>
              <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <div className="text-[16px] font-bold text-gray-900">{formatCurrency(product.price)}</div>
                  <div className="text-[11px] text-gray-400">SKU: {product.sku}</div>
                </div>
                <div className="text-right">
                  <div className={`text-[14px] font-semibold ${(product.stock - product.reservedStock) <= 5 ? 'text-red-500' : 'text-emerald-600'}`}>
                    {product.stock - product.reservedStock}
                  </div>
                  <div className="text-[11px] text-gray-400">available</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Product</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Category</th>
                <th className="text-left px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">SKU</th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Price</th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Stock</th>
                <th className="text-right px-5 py-3 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedProducts.map(product => (
                <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>
                      <div><div className="text-[13px] font-medium text-gray-900">{product.name}</div><div className="text-[11px] text-gray-400">{product.brand} · {product.model}</div></div>
                    </div>
                  </td>
                  <td className="px-5 py-3"><span className="px-2 py-0.5 rounded-full bg-blue-50 text-[11px] font-medium text-blue-600">{product.category}</span></td>
                  <td className="px-5 py-3 text-[13px] text-gray-600">{product.sku}</td>
                  <td className="px-5 py-3 text-[13px] font-medium text-gray-900 text-right">{formatCurrency(product.price)}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={`text-[13px] font-medium ${(product.stock - product.reservedStock) <= 5 ? 'text-red-500' : 'text-gray-700'}`}>
                      {product.stock - product.reservedStock}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button className="p-1.5 rounded-lg hover:bg-gray-100"><Edit3 className="w-3.5 h-3.5 text-gray-400" /></button>
                      <button onClick={() => duplicateProduct(product.id)} className="p-1.5 rounded-lg hover:bg-gray-100"><Copy className="w-3.5 h-3.5 text-gray-400" /></button>
                      <button onClick={() => deleteProduct(product.id)} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={setPage} />

      {/* Add Product Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-xl bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">Add Product</h2>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Product Name *</label>
                  <input value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Category *</label>
                  <select value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5">
                    {categories.filter(c => c !== 'All').map(c => <option key={c} value={c}>{c}</option>)}
                  </select></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Brand *</label>
                  <input value={newProduct.brand} onChange={e => setNewProduct({ ...newProduct, brand: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Model</label>
                  <input value={newProduct.model} onChange={e => setNewProduct({ ...newProduct, model: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Selling Price *</label>
                  <input type="number" value={newProduct.price || ''} onChange={e => setNewProduct({ ...newProduct, price: Number(e.target.value) })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Cost Price</label>
                  <input type="number" value={newProduct.costPrice || ''} onChange={e => setNewProduct({ ...newProduct, costPrice: Number(e.target.value) })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Stock *</label>
                  <input type="number" value={newProduct.stock || ''} onChange={e => setNewProduct({ ...newProduct, stock: Number(e.target.value) })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Warranty (months)</label>
                  <input type="number" value={newProduct.warranty} onChange={e => setNewProduct({ ...newProduct, warranty: Number(e.target.value) })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">SKU</label>
                  <input value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
              </div>
              <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Specifications</label>
                <textarea value={newProduct.specifications} onChange={e => setNewProduct({ ...newProduct, specifications: e.target.value })}
                  className="w-full h-20 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5 resize-none" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAdd(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200">Cancel</button>
              <button onClick={handleAdd} disabled={!newProduct.name || !newProduct.price}
                className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 disabled:opacity-50">Add Product</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
